import { useMemo, useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, Truck, Package, ShieldCheck, AlertTriangle, Trash2, FileText, CreditCard, DollarSign, Utensils, GlassWater, Plus, Minus, ChevronDown, CalendarIcon, Share2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import Header from "./Header";
import Footer from "./Footer";
import { Reveal } from "./Reveal";
import { ShareCartModal } from "./ShareCartModal";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { classifyZip } from "../lib/deliveryZones";
import { US_STATES, ZIP_RE } from "../lib/usStates";
import { decodeCart } from "../lib/shareCart";
import PaymentMethodGate from "./PaymentMethodGate";
import { AgencyPaymentCard } from "./AgencyPaymentCard";

export const CartPage = () => {
  const {
    items,
    menu,
    priceMap,
    unitPriceFor,
    subtotal,
    deliveryFee,
    total,
    fulfillment,
    setFulfillment,
    updateQty,
    removeItem,
    addItem,
    minimumMet,
    deliveryMinimum,
    zip,
    setDeliveryFee,
    clear,
    hydrateFromShare,
    tax,
    taxRate,
    isTaxExempt,
    paymentChoice,
  } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [shareBanner, setShareBanner] = useState(null);
  const [gateOpen, setGateOpen] = useState(false);

  const isAgency = user && (user.role === "agency" || user.role === "admin");
  // Agency's session-level payment choice from the gate. Non-agency
  // users always transact card (Stripe). Card + Cash both use retail
  // pricing; PO uses institutional (+20%) + tax-exempt.
  const agencyUsingPO = isAgency && paymentChoice === "po";
  const agencyUsingCash = isAgency && paymentChoice === "cash";

  const [form, setForm] = useState({
    customer_name: user?.name || "",
    customer_email: user?.email || "",
    customer_phone: "",
    address: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: zip || "",
    event_date: "",
    event_time: "",
    on_site_contact_name: "",
    on_site_contact_phone: "",
    delivery_instructions: "",
    has_receiving_personnel: null,
    has_delivery_cart: null,
    notes: "",
    po_number: "",
  });

  // Rush-order lead-time check (client preview). Backend is the source of
  // truth; this is only to show a warning banner so the customer isn't
  // surprised when checkout drops them into "pending approval" instead of
  // confirming right away. Must be declared AFTER `form` useState or the
  // dependency `form.event_date` lands in a TDZ at mount time (minified
  // builds surface this as "Cannot access 'B' before initialization").
  const rushNotice = useMemo(() => {
    if (!form.event_date) return null;
    try {
      const picked = parseISO(form.event_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let d = new Date(today);
      let businessDays = 0;
      while (d < picked) {
        d.setDate(d.getDate() + 1);
        if (d >= picked) break;
        const dow = d.getDay(); // 0 Sun, 6 Sat
        if (dow >= 1 && dow <= 5) businessDays += 1;
      }
      if (businessDays >= 5) return null;
      return { businessDays };
    } catch {
      return null;
    }
  }, [form.event_date]);

  // Curried setter. Supports both DOM events (`e.target.value`) and raw
  // values (e.g. Shadcn Calendar's `onSelect` passes a Date or string),
  // so date/time/calendar pickers can call it without crashing.
  const update = (k) => (eOrValue) => {
    const v =
      eOrValue && typeof eOrValue === "object" && "target" in eOrValue && eOrValue.target
        ? eOrValue.target.value
        : eOrValue ?? "";
    setForm((s) => ({ ...s, [k]: v }));
  };

  // Keep the address ZIP in sync when the user edits the top-of-cart
  // delivery ZIP (which drives the fee tier). Only push down — typing in
  // the address field has its own handler that pushes UP.
  useEffect(() => {
    if (zip && zip !== form.zip_code) {
      setForm((s) => ({ ...s, zip_code: zip }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zip]);

  // On mount: if the URL carries a ?share=… token, decode it, replace the
  // current cart with the shared contents, and strip the param so refreshes
  // don't re-hydrate stale data. See lib/shareCart.js.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("share");
    if (!token) return;
    const payload = decodeCart(token);
    // Always remove the param from the URL so it doesn't hang around.
    params.delete("share");
    const cleaned = params.toString();
    navigate(`${location.pathname}${cleaned ? `?${cleaned}` : ""}`, { replace: true });
    if (!payload || payload.items.length === 0) {
      setShareBanner({ kind: "error", text: "That shared cart link couldn't be read." });
      return;
    }
    const n = hydrateFromShare(payload);
    setShareBanner({
      kind: "ok",
      text: `Loaded ${n} item${n === 1 ? "" : "s"} from a shared cart. Prices reflect today's menu.`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nameFor = (id) => menu.items.find((m) => m.id === id)?.name || id;
  const imageFor = (id) => menu.items.find((m) => m.id === id)?.image || null;

  // Upsell sources — pulled live from the loaded menu so prices/availability
  // always match the catalog.
  // The upsell catalog draws straight from the menu so quantity and price
  // always match the catalog. On Pickup, the "Add-ons" category items
  // (Waitstaff Service, Wire Racks, 6-hour Burners) don't apply — they're
  // delivery/event-only — so we drop them from the picker.
  const utensilsItems = useMemo(
    () =>
      menu.items.filter((m) => {
        if (m.category === "Basic Dinnerware") return true;
        if (m.category === "Add-ons") return fulfillment !== "pickup";
        return false;
      }),
    [menu.items, fulfillment]
  );
  const drinksItems = useMemo(
    () => menu.items.filter((m) => m.category === "Beverages"),
    [menu.items]
  );

  // Quick lookup: current quantity-in-cart for a given menu item id
  // (cart lines without selected_options collapse to one — these upsell
  // items don't have option modifiers so this is straight-forward).
  const cartQtyFor = (id) => {
    const line = items.find((l) => l.package_id === id && !l.selected_options);
    return line ? line.quantity : 0;
  };

  const validate = () => {
    if (items.length === 0) return "Your cart is empty.";
    if (!form.customer_name.trim()) return "Name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customer_email)) return "Valid email required.";
    if (form.customer_phone.replace(/\D/g, "").length < 10) return "Valid phone required.";
    if (!form.event_date) {
      return fulfillment === "delivery"
        ? "Drop-off date is required."
        : "Pickup date is required.";
    }
    if (!form.event_time.trim()) {
      return fulfillment === "delivery"
        ? "Drop-off time is required."
        : "Pickup time is required.";
    }
    if (fulfillment === "delivery" && !form.address.trim()) return "Street address required.";
    if (fulfillment === "delivery" && !form.city.trim()) return "City required.";
    if (fulfillment === "delivery" && !form.state.trim()) return "State required.";
    if (fulfillment === "delivery" && !ZIP_RE.test(form.zip_code.trim()))
      return "Valid ZIP code required (e.g. 11433 or 11433-1234).";
    // Small-order quote path: delivery orders under the minimum go through the
    // request-a-quote funnel instead of blocking checkout, so we don't enforce
    // the minimum-met validation here — the button handler swaps flows.
    // PO number is intentionally optional — most agencies issue the PO
    // AFTER receiving the invoice, so we can't require it upfront. The
    // backend accepts a null/blank PO for the agency PO flow.
    return "";
  };

  // Small delivery orders (< $400) get routed to the request-a-quote flow
  // instead of the blocking hard minimum. Pickup orders are unaffected.
  const needsSmallOrderQuote = fulfillment === "delivery" && !minimumMet;

  const onRequestQuote = async () => {
    await submitCartQuote({ reason: "small_order" });
  };

  // Private-only "Request Invoice for PO / Approval" flow. Lets a
  // non-agency customer (schools, non-profits, government buyers) hand
  // their built cart to admin for a proper invoice they can forward
  // internally to initiate a PO. Admin converts the quote to an invoice
  // from the Quotes tab and the customer pays later (card or check).
  const onRequestPOInvoice = async () => {
    await submitCartQuote({ reason: "po_invoice" });
  };

  const submitCartQuote = async ({ reason }) => {
    setError("");
    const v = validate();
    if (v) { setError(v); return; }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({
          package_id: i.package_id,
          quantity: i.quantity,
          ...(i.selected_options ? { selected_options: i.selected_options } : {}),
        })),
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        address: form.address || null,
        address_line2: form.address_line2 || null,
        city: form.city || null,
        state: form.state || null,
        zip_code: form.zip_code || null,
        event_date: form.event_date || null,
        event_time: form.event_time || null,
        on_site_contact_name: form.on_site_contact_name || null,
        on_site_contact_phone: form.on_site_contact_phone || null,
        delivery_instructions: form.delivery_instructions || null,
        has_receiving_personnel: form.has_receiving_personnel,
        has_delivery_cart: form.has_delivery_cart,
        notes: form.notes || null,
        subtotal,
        reason,
        fulfillment,
      };
      const { data } = await api.post("/quote/cart", payload);
      clear();
      navigate(
        `/checkout/quote-received?quote_id=${encodeURIComponent(data.id)}` +
          (reason === "po_invoice" ? "&reason=po_invoice" : "")
      );
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onCheckout = async () => {
    setError("");
    const v = validate();
    if (v) { setError(v); return; }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({
          package_id: i.package_id,
          quantity: i.quantity,
          ...(i.selected_options ? { selected_options: i.selected_options } : {}),
        })),
        fulfillment,
        delivery_fee: fulfillment === "delivery" ? deliveryFee : 0,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        address: fulfillment === "delivery" ? form.address : null,
        address_line2: fulfillment === "delivery" ? (form.address_line2 || null) : null,
        city: fulfillment === "delivery" ? form.city : null,
        state: fulfillment === "delivery" ? form.state : null,
        zip_code: fulfillment === "delivery" ? form.zip_code : null,
        event_date: form.event_date || null,
        event_time: form.event_time || null,
        on_site_contact_name:
          fulfillment === "delivery" ? form.on_site_contact_name || null : null,
        on_site_contact_phone:
          fulfillment === "delivery" ? form.on_site_contact_phone || null : null,
        delivery_instructions:
          fulfillment === "delivery" ? form.delivery_instructions || null : null,
        has_receiving_personnel:
          fulfillment === "delivery" ? form.has_receiving_personnel : null,
        has_delivery_cart:
          fulfillment === "delivery" ? form.has_delivery_cart : null,
        notes: form.notes || null,
        origin_url: window.location.origin,
        ...(isAgency && paymentChoice ? { payment_method: paymentChoice } : {}),
        ...(agencyUsingPO ? { po_number: form.po_number } : {}),
      };
      const { data } = await api.post("/checkout/session", payload);
      // Rush-order gate: backend intercepted the order because the event
      // date is inside our 5-business-day lead time. Route the customer to
      // a "we'll be in touch" confirmation instead of Stripe. Works the
      // same whether this was a Stripe path (type="rush_pending") OR an
      // agency PO/cash path that got stamped pending_approval=true.
      if (data.type === "rush_pending" || data.pending_approval) {
        clear();
        navigate(`/checkout/rush-pending?order_id=${encodeURIComponent(data.order_id)}&days=${encodeURIComponent(data.rush_days_notice ?? "")}`);
        return;
      }
      if (data.type === "stripe" && data.url) {
        window.location.href = data.url;
        return;
      }
      if (data.type === "agency_po") {
        clear();
        navigate(`/checkout/po-success?order_id=${encodeURIComponent(data.order_id)}`);
        return;
      }
      if (data.type === "agency_cash") {
        clear();
        navigate(`/checkout/cash-success?order_id=${encodeURIComponent(data.order_id)}`);
        return;
      }
      setError("Unexpected checkout response.");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Agency-only "Save for Approval" flow. Creates a retail-priced
  // self_pay_invoice in `awaiting_payment` status. Customer can then
  // download the PDF, forward it for internal approval, and return to
  // `/account/invoices` later to pay by card or contact us for cash.
  const onSaveInvoice = async () => {
    setError("");
    const v = validate();
    if (v) { setError(v); return; }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({
          package_id: i.package_id,
          quantity: i.quantity,
          ...(i.selected_options ? { selected_options: i.selected_options } : {}),
        })),
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        organization: user?.organization || "",
        fulfillment,
        zip_code: fulfillment === "delivery" ? form.zip_code : null,
        address: fulfillment === "delivery" ? form.address : null,
        address_line2: fulfillment === "delivery" ? (form.address_line2 || null) : null,
        city: fulfillment === "delivery" ? form.city : null,
        state: fulfillment === "delivery" ? form.state : null,
        event_date: form.event_date || null,
        event_time: form.event_time || null,
        on_site_contact_name:
          fulfillment === "delivery" ? form.on_site_contact_name || null : null,
        on_site_contact_phone:
          fulfillment === "delivery" ? form.on_site_contact_phone || null : null,
        delivery_instructions:
          fulfillment === "delivery" ? form.delivery_instructions || null : null,
        has_receiving_personnel:
          fulfillment === "delivery" ? form.has_receiving_personnel : null,
        has_delivery_cart:
          fulfillment === "delivery" ? form.has_delivery_cart : null,
        notes: form.notes || null,
        email_me: true,
        ...(isAgency && paymentChoice ? { payment_method: paymentChoice } : {}),
      };
      const { data } = await api.post("/agency/invoices", payload);
      clear();
      navigate(`/account/invoices?just_saved=${encodeURIComponent(data.order_id)}`);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App font-sans bg-white text-brand-charcoal antialiased">
      <Header />
      {gateOpen && <PaymentMethodGate onClose={() => setGateOpen(false)} />}
      <main>
        <section className="pt-24 sm:pt-28 lg:pt-32 pb-8 bg-brand-cream">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10">
            <Reveal>
              <Link
                to="/menu"
                data-testid="cart-back-to-menu-btn"
                className="inline-flex items-center gap-2 rounded-full bg-white border border-black/10 px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal hover:bg-brand-charcoal hover:text-white transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to Menu
              </Link>
            </Reveal>
            <Reveal delay={1}>
              <p className="mt-6 text-[12px] font-bold uppercase tracking-[0.22em] text-brand-teal">
                {isAgency
                  ? `Agency Checkout · ${agencyUsingPO ? "Purchase Order" : agencyUsingCash ? "Cash" : "Card Payment"}`
                  : "Private Checkout · Card Payment"}
              </p>
            </Reveal>
            <Reveal delay={2}>
              <h1 className="mt-2 font-display font-black text-4xl sm:text-5xl tracking-tight text-brand-charcoal leading-[1.05]">
                Your Cart<span className="text-brand-gold">.</span>
              </h1>
            </Reveal>
            {isAgency && (
              <p className="mt-2 text-[12.5px] text-brand-charcoal/55">
                Logged in as <strong>{user.name}</strong> ({user.organization || user.email}) —{" "}
                {agencyUsingPO
                  ? "agency pricing applied (Purchase Order)."
                  : "retail pricing applied."}
              </p>
            )}

            {isAgency && agencyUsingPO && user.role === "agency" && (
              <button
                type="button"
                data-testid="cart-switch-payment-banner"
                onClick={() => setGateOpen(true)}
                className="mt-5 w-full sm:w-auto inline-flex items-center gap-3 rounded-2xl bg-brand-gold text-brand-charcoal px-5 py-3.5 text-left hover:bg-brand-gold/90 focus:outline-none focus:ring-2 focus:ring-brand-charcoal/40 transition-colors"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-[10.5px] font-black uppercase tracking-[0.14em] text-brand-charcoal/70">
                    Need to pay by card or cash?
                  </span>
                  <span className="mt-0.5 block text-[13px] font-bold text-brand-charcoal leading-snug">
                    Tap to request approval. You keep agency pricing until it&apos;s approved.
                  </span>
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 text-brand-charcoal" />
              </button>
            )}
          </div>
        </section>

        <section className="py-10 sm:py-14 bg-white">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-8">
            {/* Items + Form */}
            <div className="lg:col-span-7 space-y-6">
              {/* Items */}
              <div className="rounded-3xl bg-brand-cream/40 border border-black/5 p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display font-black text-[18px] text-brand-charcoal tracking-tight">
                    Items ({items.length})
                  </h2>
                  {items.length > 0 && (
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        data-testid="cart-share-btn"
                        onClick={() => setShareOpen(true)}
                        className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-brand-teal hover:text-brand-teal/80"
                      >
                        <Share2 className="h-3.5 w-3.5" />
                        Share cart
                      </button>
                      <button
                        type="button"
                        data-testid="cart-clear-btn"
                        onClick={clear}
                        className="text-[11px] font-bold uppercase tracking-[0.12em] text-brand-charcoal/55 hover:text-red-600"
                      >
                        Clear cart
                      </button>
                    </div>
                  )}
                </div>
                {shareBanner && (
                  <div
                    data-testid="cart-share-banner"
                    className={`mb-4 rounded-2xl px-4 py-3 text-[13px] flex items-start justify-between gap-3 border ${
                      shareBanner.kind === "ok"
                        ? "bg-brand-teal/8 border-brand-teal/25 text-brand-teal"
                        : "bg-red-50 border-red-200 text-red-700"
                    }`}
                  >
                    <span>{shareBanner.text}</span>
                    <button
                      type="button"
                      onClick={() => setShareBanner(null)}
                      className="text-[11px] font-bold uppercase tracking-[0.1em] opacity-70 hover:opacity-100"
                      aria-label="Dismiss shared cart notice"
                    >
                      Dismiss
                    </button>
                  </div>
                )}
                {items.length === 0 ? (
                  <p data-testid="cart-page-empty" className="text-[14px] text-brand-charcoal/65">
                    Your cart is empty. <Link to="/menu" className="font-bold text-brand-teal underline">Browse the menu</Link>.
                  </p>
                ) : (
                  <ul className="space-y-3" data-testid="cart-page-items">
                    {items.map((it) => {
                      const unit = unitPriceFor(it);
                      const optsKey = JSON.stringify(it.selected_options || null);
                      const lineKey = `${it.package_id}:${optsKey}`;
                      const optionLines = [];
                      if (it.selected_options) {
                        for (const [grp, vals] of Object.entries(it.selected_options)) {
                          if (Array.isArray(vals) && vals.length > 0) {
                            optionLines.push(`${grp}: ${vals.join(", ")}`);
                          }
                        }
                      }
                      return (
                        <li
                          key={lineKey}
                          data-testid={`cart-page-line-${it.package_id}`}
                          className="grid grid-cols-12 items-center gap-3 rounded-2xl bg-white border border-black/5 p-3.5"
                        >
                          <div className="col-span-6 sm:col-span-7 min-w-0 flex items-start gap-3">
                            {imageFor(it.package_id) ? (
                              <img
                                src={imageFor(it.package_id)}
                                alt={nameFor(it.package_id)}
                                loading="lazy"
                                data-testid={`cart-page-line-image-${it.package_id}`}
                                className="h-14 w-14 sm:h-16 sm:w-16 shrink-0 rounded-xl object-cover bg-brand-cream border border-black/5"
                              />
                            ) : null}
                            <div className="min-w-0 flex-1">
                              <p className="font-display font-bold text-[14px] sm:text-[15px] text-brand-charcoal leading-tight">
                                {nameFor(it.package_id)}
                              </p>
                              {optionLines.length > 0 && (
                                <p className="mt-0.5 text-[11px] font-bold uppercase tracking-[0.12em] text-brand-teal">
                                  {optionLines.join(" · ")}
                                </p>
                              )}
                              <p className="mt-0.5 text-[12px] text-brand-charcoal/55">${unit.toFixed(2)} each</p>
                            </div>
                          </div>
                          <div className="col-span-4 sm:col-span-3 inline-flex items-center gap-2 rounded-full bg-brand-cream p-1 w-fit">
                            <button onClick={() => updateQty(it.package_id, it.quantity - 1, optsKey)} className="h-7 w-7 rounded-full hover:bg-white">−</button>
                            <span className="w-6 text-center text-[14px] font-bold">{it.quantity}</span>
                            <button onClick={() => updateQty(it.package_id, it.quantity + 1, optsKey)} className="h-7 w-7 rounded-full hover:bg-white">+</button>
                          </div>
                          <div className="col-span-2 sm:col-span-2 flex items-center justify-end gap-2">
                            <span className="font-display font-black text-[14px] tabular-nums">
                              ${(unit * it.quantity).toFixed(2)}
                            </span>
                            <button
                              onClick={() => removeItem(it.package_id, optsKey)}
                              aria-label="Remove"
                              className="text-brand-charcoal/40 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Fulfillment */}
              <div className="rounded-3xl bg-white border border-black/5 p-5 sm:p-6">
                <h2 className="font-display font-black text-[18px] text-brand-charcoal tracking-tight">
                  Fulfillment
                </h2>
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    data-testid="cart-page-delivery-btn"
                    onClick={() => setFulfillment("delivery")}
                    className={`flex items-start gap-3 rounded-2xl border-2 p-4 text-left transition-colors ${
                      fulfillment === "delivery" ? "border-brand-teal bg-brand-teal/5" : "border-black/10 hover:border-brand-charcoal/40"
                    }`}
                  >
                    <Truck className="h-5 w-5 mt-0.5 text-brand-teal" />
                    <div>
                      <p className="font-bold text-[14px] text-brand-charcoal">Delivery</p>
                      <p className="mt-0.5 text-[11.5px] text-brand-charcoal/60">$400 min · ZIP-based fee</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    data-testid="cart-page-pickup-btn"
                    onClick={() => setFulfillment("pickup")}
                    className={`flex items-start gap-3 rounded-2xl border-2 p-4 text-left transition-colors ${
                      fulfillment === "pickup" ? "border-brand-teal bg-brand-teal/5" : "border-black/10 hover:border-brand-charcoal/40"
                    }`}
                  >
                    <Package className="h-5 w-5 mt-0.5 text-brand-teal" />
                    <div>
                      <p className="font-bold text-[14px] text-brand-charcoal">Pickup</p>
                      <p className="mt-0.5 text-[11.5px] text-brand-charcoal/60">No minimum · Free</p>
                    </div>
                  </button>
                </div>
                {fulfillment === "delivery" && (
                  <div className="mt-4">
                    <div className="grid sm:grid-cols-3 gap-3">
                      <div className="sm:col-span-1">
                        <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
                          Delivery ZIP *
                        </Label>
                        <Input
                          data-testid="cart-page-zip"
                          inputMode="numeric"
                          maxLength={5}
                          value={zip || ""}
                          placeholder="e.g. 11432"
                          onChange={(e) => {
                            const next = e.target.value.replace(/\D/g, "").slice(0, 5);
                            const r = classifyZip(next);
                            // Fee = matched tier fee, OR 0 when zip incomplete / out-of-zone (custom quote)
                            const nextFee = r && typeof r.fee === "number" ? r.fee : 0;
                            setDeliveryFee(nextFee, next);
                          }}
                          className="mt-1.5 h-11 rounded-xl bg-brand-cream/40 border-black/10"
                        />
                      </div>
                      <div className="sm:col-span-2 flex items-end">
                        {(() => {
                          const r = classifyZip(zip);
                          if (!zip) {
                            return (
                              <p className="text-[12px] text-brand-charcoal/60 leading-snug">
                                Enter a 5-digit ZIP to calculate your delivery fee. The fee
                                updates instantly and is added to your total.
                              </p>
                            );
                          }
                          if (!r) {
                            return (
                              <p
                                data-testid="cart-page-zip-incomplete"
                                className="text-[12px] text-brand-charcoal/60 leading-snug"
                              >
                                Keep typing — we need a full 5-digit ZIP to look up your zone.
                              </p>
                            );
                          }
                          if (r.fee == null) {
                            return (
                              <p
                                data-testid="cart-page-zip-custom"
                                className="text-[12.5px] text-brand-charcoal/85 leading-snug"
                              >
                                <span className="font-bold text-brand-gold-dark">
                                  Custom quote needed
                                </span>{" "}
                                — {r.area || "outside our standard delivery zones"}. Switch to
                                Pickup or{" "}
                                <Link to="/quote" className="underline text-brand-teal">
                                  request a quote
                                </Link>
                                .
                              </p>
                            );
                          }
                          return (
                            <p
                              data-testid="cart-page-zip-result"
                              className="text-[12.5px] text-brand-charcoal/85 leading-snug"
                            >
                              <span className="font-bold text-brand-charcoal">{r.title}</span> —{" "}
                              {r.area}. Delivery fee:{" "}
                              <strong
                                data-testid="cart-page-delivery-fee-value"
                                className="text-brand-charcoal"
                              >
                                ${r.fee.toFixed(2)}
                              </strong>
                            </p>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {isAgency && <AgencyPaymentCard onChange={() => setGateOpen(true)} />}

              {/* Contact + event */}
              <div className="rounded-3xl bg-white border border-black/5 p-5 sm:p-6">
                <h2 className="font-display font-black text-[18px] text-brand-charcoal tracking-tight">
                  Contact & Event Details
                </h2>
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  <Field label="Full Name *">
                    <Input data-testid="checkout-name" value={form.customer_name} onChange={update("customer_name")} className="h-11 rounded-xl bg-brand-cream/40 border-black/10" />
                  </Field>
                  <Field label="Email *">
                    <Input data-testid="checkout-email" type="email" value={form.customer_email} onChange={update("customer_email")} className="h-11 rounded-xl bg-brand-cream/40 border-black/10" />
                  </Field>
                  <Field label="Phone *">
                    <Input data-testid="checkout-phone" value={form.customer_phone} onChange={update("customer_phone")} className="h-11 rounded-xl bg-brand-cream/40 border-black/10" />
                  </Field>
                  <Field
                    label={fulfillment === "delivery" ? "Drop-off Date *" : "Pickup Date *"}
                  >
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid="checkout-event-date"
                          className="flex h-11 w-full items-center justify-between rounded-xl border border-black/10 bg-brand-cream/40 px-3 text-left text-[14px] hover:bg-brand-cream/70 focus:outline-none focus:ring-2 focus:ring-brand-teal"
                        >
                          <span className={form.event_date ? "text-brand-charcoal" : "text-brand-charcoal/40"}>
                            {form.event_date
                              ? format(parseISO(form.event_date), "EEE, MMM d, yyyy")
                              : "Pick a date"}
                          </span>
                          <CalendarIcon className="h-4 w-4 text-brand-charcoal/50 shrink-0" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={form.event_date ? parseISO(form.event_date) : undefined}
                          onSelect={(d) =>
                            update("event_date")(d ? format(d, "yyyy-MM-dd") : "")
                          }
                          disabled={(d) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return d < today;
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </Field>
                  <Field
                    label={fulfillment === "delivery" ? "Drop-off Time *" : "Pickup Time *"}
                  >
                    <Input
                      data-testid="checkout-event-time"
                      type="time"
                      value={form.event_time}
                      onChange={update("event_time")}
                      className="h-11 rounded-xl bg-brand-cream/40 border-black/10"
                    />
                  </Field>
                  {rushNotice && (
                    <div
                      data-testid="rush-order-notice"
                      className="sm:col-span-2 rounded-xl border border-amber-300 bg-amber-50 p-3.5 flex items-start gap-2.5"
                    >
                      <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                      <div className="text-[12.5px] leading-relaxed text-amber-900">
                        <strong className="block mb-0.5">
                          Your event is only {rushNotice.businessDays} business day
                          {rushNotice.businessDays === 1 ? "" : "s"} away.
                        </strong>
                        Our standard lead time is 5 business days. We can often still
                        accommodate rush orders, but your order will be sent to us for
                        approval first — nothing will be charged until we confirm. We
                        aim to respond within a few hours during business hours.
                      </div>
                    </div>
                  )}
                  {fulfillment === "delivery" && (
                    <>
                      <div className="sm:col-span-2">
                        <Field label="Street Address *">
                          <Input
                            data-testid="checkout-address"
                            value={form.address}
                            onChange={update("address")}
                            placeholder="123 Main St"
                            autoComplete="address-line1"
                            className="h-11 rounded-xl bg-brand-cream/40 border-black/10"
                          />
                        </Field>
                      </div>
                      <div className="sm:col-span-2">
                        <Field label="Apt / Suite / Unit (optional)">
                          <Input
                            data-testid="checkout-address-line2"
                            value={form.address_line2}
                            onChange={update("address_line2")}
                            placeholder="Apt 4B, Suite 200, etc."
                            autoComplete="address-line2"
                            className="h-11 rounded-xl bg-brand-cream/40 border-black/10"
                          />
                        </Field>
                      </div>
                      <Field label="City *">
                        <Input
                          data-testid="checkout-city"
                          value={form.city}
                          onChange={update("city")}
                          placeholder="Jamaica"
                          autoComplete="address-level2"
                          className="h-11 rounded-xl bg-brand-cream/40 border-black/10"
                        />
                      </Field>
                      <Field label="State *">
                        <select
                          data-testid="checkout-state"
                          value={form.state}
                          onChange={update("state")}
                          autoComplete="address-level1"
                          className="h-11 rounded-xl bg-brand-cream/40 border border-black/10 px-3 text-[14px]"
                        >
                          <option value="">Select state…</option>
                          {US_STATES.map(([code, name]) => (
                            <option key={code} value={code}>
                              {`${code} — ${name}`}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="ZIP Code *">
                        <Input
                          data-testid="checkout-zip"
                          value={form.zip_code}
                          onChange={(e) => {
                            const raw = e.target.value;
                            // Allow 5-digit or 5-digit+4 formats; keep only digits + dash.
                            const cleaned = raw.replace(/[^0-9-]/g, "").slice(0, 10);
                            setForm((s) => ({ ...s, zip_code: cleaned }));
                            // Sync the cart-level ZIP so the delivery fee
                            // calculator above stays in agreement.
                            const fiveDigit = cleaned.replace(/\D/g, "").slice(0, 5);
                            if (fiveDigit.length === 5) {
                              const r = classifyZip(fiveDigit);
                              const fee = r && typeof r.fee === "number" ? r.fee : 0;
                              setDeliveryFee(fee, fiveDigit);
                            } else if (fiveDigit !== zip) {
                              setDeliveryFee(0, fiveDigit);
                            }
                          }}
                          placeholder="11433"
                          autoComplete="postal-code"
                          inputMode="numeric"
                          maxLength={10}
                          className="h-11 rounded-xl bg-brand-cream/40 border-black/10"
                        />
                      </Field>
                      <Field label="On-site Contact Name">
                        <Input
                          data-testid="checkout-onsite-name"
                          value={form.on_site_contact_name}
                          onChange={update("on_site_contact_name")}
                          placeholder="Who meets the driver?"
                          className="h-11 rounded-xl bg-brand-cream/40 border-black/10"
                        />
                      </Field>
                      <Field label="On-site Contact Phone">
                        <Input
                          data-testid="checkout-onsite-phone"
                          type="tel"
                          value={form.on_site_contact_phone}
                          onChange={update("on_site_contact_phone")}
                          placeholder="(212) 555-0123"
                          className="h-11 rounded-xl bg-brand-cream/40 border-black/10"
                        />
                      </Field>
                      <div className="sm:col-span-2">
                        <Field label="Delivery Instructions & Access (entry points, ramps, floor, buzzer, loading dock…)">
                          <Textarea
                            data-testid="checkout-delivery-instructions"
                            value={form.delivery_instructions}
                            onChange={update("delivery_instructions")}
                            rows={3}
                            maxLength={1000}
                            placeholder="Use side entrance on 47th. Freight elevator, 3rd floor. Ramp to loading dock. Ask for security."
                            className="rounded-xl bg-brand-cream/40 border-black/10"
                          />
                        </Field>
                      </div>
                      <div className="sm:col-span-2 rounded-2xl bg-brand-cream/40 border border-black/10 p-3.5">
                        <p className="text-[11.5px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/70">
                          Receiving logistics
                        </p>
                        <p className="mt-0.5 text-[11.5px] text-brand-charcoal/55 leading-snug">
                          Helps our driver plan bring-in. Answer both so we bring the right equipment.
                        </p>
                        <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
                          <YesNoPill
                            testId="checkout-receiving-personnel"
                            label="Staff on-site to help receive/carry?"
                            value={form.has_receiving_personnel}
                            onChange={(v) => setForm((s) => ({ ...s, has_receiving_personnel: v }))}
                          />
                          <YesNoPill
                            testId="checkout-receiving-cart"
                            label="Cart or dolly available on-site?"
                            value={form.has_delivery_cart}
                            onChange={(v) => setForm((s) => ({ ...s, has_delivery_cart: v }))}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  {fulfillment === "pickup" && (
                    <div
                      data-testid="checkout-pickup-info"
                      className="sm:col-span-2 rounded-2xl bg-brand-cream/60 border border-black/5 p-4 text-[12.5px] text-brand-charcoal/75 leading-relaxed"
                    >
                      <p className="font-bold text-brand-charcoal">Pickup location</p>
                      <p className="mt-0.5">110-46 Merrick Blvd, Jamaica, NY 11433</p>
                      <p className="mt-1 text-brand-charcoal/55">
                        Please plan to arrive within ±15 minutes of your selected pickup
                        time so your food stays at temperature.
                      </p>
                    </div>
                  )}
                  {agencyUsingPO && (
                    <div className="sm:col-span-2">
                      <Field label="Purchase Order Number (optional)">
                        <Input
                          data-testid="checkout-po"
                          value={form.po_number}
                          onChange={update("po_number")}
                          placeholder="Leave blank if PO is issued after invoice"
                          className="h-11 rounded-xl bg-brand-cream/40 border-black/10"
                        />
                        <p className="mt-1.5 text-[11.5px] text-brand-charcoal/55 leading-snug">
                          Most agencies issue POs after receiving the invoice — you can
                          leave this blank and add it later. We&apos;ll email the invoice
                          either way.
                        </p>
                      </Field>
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <Field label="Notes (dietary needs, setup, etc.)">
                      <Textarea data-testid="checkout-notes" value={form.notes} onChange={update("notes")} rows={3} className="rounded-xl bg-brand-cream/40 border-black/10" />
                    </Field>
                  </div>
                </div>
              </div>

              {/* Final Touches — quick-add upsell of dinnerware + drinks */}
              <div
                data-testid="checkout-final-touches"
                className="rounded-3xl bg-white border border-black/5 p-5 sm:p-6"
              >
                <h2 className="font-display font-black text-[18px] text-brand-charcoal tracking-tight">
                  Final Touches
                </h2>
                <p className="mt-1 text-[12.5px] text-brand-charcoal/55 leading-snug">
                  Quick add — round out your order with dinnerware or beverages.
                </p>
                <div className="mt-4 space-y-3">
                  <UpsellGroup
                    testIdRoot="upsell-utensils"
                    label="Utensils, plates & setup"
                    helper={
                      fulfillment === "pickup"
                        ? "Disposable dinnerware to take with your pickup."
                        : "Disposable dinnerware, racks, burners and full-service add-ons."
                    }
                    icon={Utensils}
                    items={utensilsItems}
                    cartQtyFor={cartQtyFor}
                    onAdd={(id) => addItem(id, 1, null)}
                    onUpdateQty={(id, q) => updateQty(id, q, JSON.stringify(null))}
                    onRemove={(id) => removeItem(id, JSON.stringify(null))}
                    imageFor={imageFor}
                  />
                  <UpsellGroup
                    testIdRoot="upsell-drinks"
                    label="Drinks & beverages"
                    helper="Coffee, juices, soda and bottled drinks — add now and we'll bring them with your order."
                    icon={GlassWater}
                    items={drinksItems}
                    cartQtyFor={cartQtyFor}
                    onAdd={(id) => addItem(id, 1, null)}
                    onUpdateQty={(id, q) => updateQty(id, q, JSON.stringify(null))}
                    onRemove={(id) => removeItem(id, JSON.stringify(null))}
                    imageFor={imageFor}
                  />
                </div>
              </div>
            </div>

            {/* Summary */}
            <aside className="lg:col-span-5">
              <div className="lg:sticky lg:top-24 rounded-3xl bg-brand-charcoal text-white border border-white/10 p-6 sm:p-7">
                <h2 className="font-display font-black text-[20px] tracking-tight">Order Summary</h2>
                <div className="mt-5 space-y-2 text-[13.5px]">
                  <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
                  <Row label={fulfillment === "delivery" ? "Delivery fee" : "Pickup"} value={fulfillment === "delivery" ? `$${deliveryFee.toFixed(2)}` : "$0.00"} />
                  {isTaxExempt ? (
                    <Row
                      label="Tax (Tax Exempt)"
                      value="$0.00"
                      data-testid="cart-page-tax-row"
                    />
                  ) : (
                    <Row
                      label={`Sales Tax (${(taxRate * 100).toFixed(3).replace(/\.?0+$/, "")}%)`}
                      value={`$${tax.toFixed(2)}`}
                      data-testid="cart-page-tax-row"
                    />
                  )}
                  <div className="h-px bg-white/10 my-2" />
                  <Row label="Total" value={`$${total.toFixed(2)}`} bold />
                </div>

                {needsSmallOrderQuote && (
                  <div
                    data-testid="cart-page-small-order-quote-card"
                    className="mt-4 rounded-2xl bg-brand-gold/15 border border-brand-gold/40 px-4 py-3.5 text-[12.5px] text-white leading-snug"
                  >
                    <p className="font-bold text-brand-gold uppercase tracking-[0.14em] text-[10.5px]">
                      Under ${deliveryMinimum} · Request a quote
                    </p>
                    <p className="mt-1.5 text-white/85">
                      Small delivery orders get a personal review. Submit this cart and
                      we&apos;ll email you a custom invoice with the delivery fee <b>within 24 hours</b>
                      — pay online or via check when it&apos;s ready.
                    </p>
                  </div>
                )}

                {error && (
                  <p data-testid="checkout-error" role="alert" className="mt-4 text-[12.5px] text-red-300">
                    {error}
                  </p>
                )}

                <button
                  type="button"
                  data-testid={needsSmallOrderQuote ? "cart-request-quote-btn" : "checkout-submit-btn"}
                  onClick={needsSmallOrderQuote ? onRequestQuote : onCheckout}
                  disabled={submitting || items.length === 0}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-gold text-brand-charcoal px-5 py-3.5 text-[12.5px] font-bold uppercase tracking-[0.12em] hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting
                    ? "Processing…"
                    : needsSmallOrderQuote
                    ? "Request Quote"
                    : agencyUsingPO
                    ? "Submit PO Order"
                    : agencyUsingCash
                    ? "Submit Cash Order"
                    : "Pay with Card"}
                  <ArrowRight className="h-4 w-4" />
                </button>

                {/* Agency save-for-approval: creates a retail-priced invoice
                    the user can download, forward internally for approval,
                    and pay later from their account. Elevated directly under
                    the main CTA so mobile users see both choices above the fold. */}
                {isAgency && items.length > 0 && !needsSmallOrderQuote && (
                  <button
                    type="button"
                    data-testid="cart-save-invoice-btn"
                    onClick={onSaveInvoice}
                    disabled={submitting}
                    className="mt-3 w-full inline-flex flex-col items-center justify-center gap-0.5 rounded-full border-2 border-brand-teal bg-brand-teal/15 text-white px-5 py-3 hover:bg-brand-teal/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="inline-flex items-center gap-2 text-[11.5px] font-bold uppercase tracking-[0.12em] text-brand-teal">
                      <FileText className="h-3.5 w-3.5" />
                      {agencyUsingPO ? "Save Invoice for Approval" : "Save Invoice — Pay Later"}
                    </span>
                    <span className="text-[10.5px] font-medium text-white/70 leading-tight">
                      {agencyUsingPO
                        ? "Agency pricing · get internal approval, then submit your PO"
                        : "Download PDF for approval, pay later"}
                    </span>
                  </button>
                )}

                {/* Private-user "Request Invoice for PO / Approval" flow.
                    School / agency / non-profit buyers often need an invoice
                    to initiate an internal purchase order before they can
                    pay — this button hands the cart to admin, who builds
                    and emails a proper invoice. Hidden when the main CTA is
                    already the small-order quote (<$400 delivery) because
                    that funnel does the same thing. */}
                {!isAgency && items.length > 0 && !needsSmallOrderQuote && (
                  <button
                    type="button"
                    data-testid="cart-request-po-invoice-btn"
                    onClick={onRequestPOInvoice}
                    disabled={submitting}
                    className="mt-3 w-full inline-flex flex-col items-center justify-center gap-0.5 rounded-full border-2 border-brand-teal bg-brand-teal/15 text-white px-5 py-3 hover:bg-brand-teal/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="inline-flex items-center gap-2 text-[11.5px] font-bold uppercase tracking-[0.12em] text-brand-teal">
                      <FileText className="h-3.5 w-3.5" />
                      Request Invoice for PO / Approval
                    </span>
                    <span className="text-[10.5px] font-medium text-white/70 leading-tight">
                      For schools, agencies &amp; non-profits — no payment now
                    </span>
                  </button>
                )}

                <Link
                  to="/menu"
                  data-testid="cart-continue-shopping-btn"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 text-white px-5 py-3 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:bg-white/10 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Continue Shopping
                </Link>

                {/* Agency-only shortcut into the multi-drop batch flow. Uses
                    the cart's current items as the "shared items" seed on the
                    batch page (which reads them from CartContext). */}
                {user && user.role === "agency" && items.length > 0 && (
                  <Link
                    to="/agency/batch?mode=same"
                    data-testid="cart-batch-shortcut-btn"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-gold/25 border border-brand-gold/50 text-brand-gold px-5 py-3 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:bg-brand-gold hover:text-brand-charcoal transition-colors"
                  >
                    Deliver to multiple locations?
                  </Link>
                )}

                <p className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-white/55">
                  <ShieldCheck className="h-3.5 w-3.5 text-brand-teal" />
                  {needsSmallOrderQuote
                    ? "No payment now — invoice arrives by email"
                    : agencyUsingPO
                    ? "PO-based — no card required"
                    : agencyUsingCash
                    ? "Cash collected at delivery or pickup"
                    : "Secure card checkout via Stripe"}
                </p>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
      <ShareCartModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        cartState={{ items, fulfillment, zip }}
        itemCount={items.reduce((n, it) => n + (it.quantity || 0), 0)}
      />
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">{label}</Label>
    {children}
  </div>
);

// Yes/No/Unset three-state pill toggle used for the "receiving logistics" section.
// null = not answered, true = Yes, false = No.
const YesNoPill = ({ testId, label, value, onChange }) => {
  const btn = (v, txt) => {
    const active = value === v;
    return (
      <button
        type="button"
        data-testid={`${testId}-${txt.toLowerCase()}`}
        onClick={() => onChange(active ? null : v)}
        className={`h-8 px-4 rounded-full text-[11.5px] font-bold uppercase tracking-[0.1em] transition-colors ${
          active
            ? v
              ? "bg-brand-teal text-white"
              : "bg-brand-charcoal text-white"
            : "bg-white text-brand-charcoal/70 hover:text-brand-charcoal border border-black/10"
        }`}
      >
        {txt}
      </button>
    );
  };
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[11.5px] font-medium text-brand-charcoal/75 leading-snug">
        {label}
      </span>
      <div className="flex items-center gap-2">
        {btn(true, "Yes")}
        {btn(false, "No")}
      </div>
    </div>
  );
};

const Row = ({ label, value, bold }) => (
  <div className={`flex items-center justify-between ${bold ? "font-display font-black text-[18px]" : "text-white/75"}`}>
    <span>{label}</span>
    <span className="tabular-nums">{value}</span>
  </div>
);

const UpsellGroup = ({
  testIdRoot,
  label,
  helper,
  icon: Icon,
  items,
  cartQtyFor,
  onAdd,
  onUpdateQty,
  onRemove,
  imageFor,
}) => {
  const [open, setOpen] = useState(false);
  // # of these upsell lines that already have qty in the cart — surfaced
  // as a small badge so the buyer can see at a glance what's been added.
  const inCartCount = items.reduce(
    (n, it) => n + (cartQtyFor(it.id) > 0 ? 1 : 0),
    0
  );

  if (!items || items.length === 0) return null;

  return (
    <div
      data-testid={testIdRoot}
      className={`rounded-2xl border-2 transition-colors ${
        open ? "border-brand-teal bg-brand-teal/5" : "border-black/10 bg-white"
      }`}
    >
      <button
        type="button"
        data-testid={`${testIdRoot}-toggle`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`${testIdRoot}-list`}
        className="w-full flex items-start gap-3 p-4 text-left"
      >
        <span
          className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
            open || inCartCount > 0
              ? "bg-brand-teal text-white"
              : "bg-brand-cream text-brand-charcoal/70"
          }`}
        >
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p
              className="font-bold text-[14px] text-brand-charcoal leading-tight"
            >
              {label}
            </p>
            {inCartCount > 0 && (
              <span
                data-testid={`${testIdRoot}-count-badge`}
                className="inline-flex items-center justify-center rounded-full bg-brand-teal text-white px-1.5 min-w-[18px] h-[18px] text-[10px] font-bold"
              >
                {inCartCount}
              </span>
            )}
          </div>
          <p className="mt-1 text-[12px] text-brand-charcoal/65 leading-snug">{helper}</p>
        </div>
        <ChevronDown
          className={`h-4 w-4 mt-2 shrink-0 text-brand-charcoal/55 transition-transform duration-200 ${
            open ? "rotate-180 text-brand-teal" : ""
          }`}
        />
      </button>

      {open && (
        <ul
          id={`${testIdRoot}-list`}
          data-testid={`${testIdRoot}-items`}
          className="border-t border-black/5 p-2 sm:p-3 space-y-1.5"
        >
          {items.map((it) => {
            const qty = cartQtyFor(it.id);
            const img = imageFor ? imageFor(it.id) : null;
            return (
              <li
                key={it.id}
                data-testid={`${testIdRoot}-item-${it.id}`}
                className="flex items-center gap-3 rounded-xl bg-white border border-black/5 p-2.5"
              >
                {img ? (
                  <img
                    src={img}
                    alt=""
                    loading="lazy"
                    className="h-12 w-12 shrink-0 rounded-lg object-cover bg-brand-cream"
                  />
                ) : (
                  <div className="h-12 w-12 shrink-0 rounded-lg bg-brand-cream border border-black/5" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-[13px] text-brand-charcoal leading-tight truncate">
                    {it.name}
                  </p>
                  <p className="mt-0.5 text-[11.5px] text-brand-charcoal/60">
                    ${it.price.toFixed(2)} {it.unit ? `· ${it.unit}` : ""}
                  </p>
                </div>
                {qty === 0 ? (
                  <button
                    type="button"
                    data-testid={`${testIdRoot}-add-${it.id}`}
                    onClick={() => onAdd(it.id)}
                    className="inline-flex items-center gap-1 rounded-full bg-brand-charcoal text-white px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-colors"
                  >
                    <Plus className="h-3 w-3" />
                    Add
                  </button>
                ) : (
                  <div
                    data-testid={`${testIdRoot}-stepper-${it.id}`}
                    className="inline-flex items-center gap-1 rounded-full bg-brand-charcoal text-white px-1.5 py-1"
                  >
                    <button
                      type="button"
                      data-testid={`${testIdRoot}-dec-${it.id}`}
                      onClick={() =>
                        qty === 1 ? onRemove(it.id) : onUpdateQty(it.id, qty - 1)
                      }
                      aria-label="Decrease quantity"
                      className="inline-flex h-6 w-6 items-center justify-center rounded-full hover:bg-white/10"
                    >
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="min-w-[16px] text-center text-[12px] font-bold tabular-nums">
                      {qty}
                    </span>
                    <button
                      type="button"
                      data-testid={`${testIdRoot}-inc-${it.id}`}
                      onClick={() => onUpdateQty(it.id, qty + 1)}
                      aria-label="Increase quantity"
                      className="inline-flex h-6 w-6 items-center justify-center rounded-full hover:bg-white/10"
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CartPage;
